import React, { useState } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart } from 'recharts';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import { useChartTheme } from '../hooks/useChartTheme';

interface StageTrendsData {
  period_start: string;
  period_label: string;
  period_type: string;
  stage_name: string;
  stage_order: number;
  deals_in_stage: number;
  scored_deals: number;
  avg_score: number;
  total_amount: number;
  total_stage_entries: number;
}

interface StageTrendsChartProps {
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
}

type PeriodType = 'week' | 'month' | 'quarter';

const StageTrendsChart: React.FC<StageTrendsChartProps> = ({
  selectedCycleTypes,
  selectedAccountValues,
  selectedSalesRep,
  selectedTeam
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month');
  const chartTheme = useChartTheme();

  // Smart score formatting: round to nearest integer if within 0.03 of a whole number
  const formatScore = (score: number): string => {
    const rounded = Math.round(score);
    // If the score is within 0.03 of a whole number, show the whole number
    // This catches cases like 0.98, 0.99 -> 1.0 and 1.97, 1.98 -> 2.0
    if (Math.abs(score - rounded) <= 0.03) {
      return rounded.toFixed(1); // Shows 1.0, 2.0, etc.
    }
    // Otherwise keep decimal precision
    return score.toFixed(2);
  };

  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('salesai-api-key');

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  const { data: stageTrendsData, isLoading, error, isFetching } = useQuery<StageTrendsData[]>(
    [
      'dealStageTrends_v5_debug', // Updated cache key to force fresh data with deals_in_stage field
      selectedPeriod,
      cycleTypesString || 'all',
      selectedAccountValues?.join(','),
      selectedSalesRep,
      selectedTeam
    ],
    () => salesCyclesApi.getDealStageTrends(
      selectedPeriod,
      12, // 12 months period
      cycleTypesString,
      selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
      selectedSalesRep,
      selectedTeam
    ),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated,
      onSuccess: (data) => {
        // Debug: Check if deals_in_stage is present in API response
        if (data && data.length > 0) {
          console.log('✅ API Response - First item:', {
            stage: data[0].stage_name,
            deals_in_stage: data[0].deals_in_stage,
            scored_deals: data[0].scored_deals
          });
        }
      }
    }
  );





  // Group data by stage for individual charts
  const stageData = React.useMemo(() => {
    if (!stageTrendsData || stageTrendsData.length === 0) return {};

    const stages = ['Discovery', 'Business Case', 'Validation', 'Economic Buyer Meeting', 'Legal & Procurement'];
    const result: { [key: string]: any[] } = {};

    stages.forEach(stageName => {
      const stageEntries = stageTrendsData
        .filter(item => item.stage_name === stageName)
        .sort((a, b) => new Date(a.period_start).getTime() - new Date(b.period_start).getTime())
        .map(item => ({
          period_label: item.period_label,
          period_start: item.period_start,
          deals_in_stage: item.deals_in_stage || 0,
          scored_deals: item.scored_deals,
          avg_score: item.avg_score,
          total_amount: item.total_amount,
          total_stage_entries: item.total_stage_entries
        }));


      result[stageName] = stageEntries;
    });

    return result;
  }, [stageTrendsData]);

  // Define stage colors
  const stageColors = {
    'Discovery': '#8b5cf6',
    'Business Case': '#06b6d4', 
    'Validation': '#10b981',
    'Economic Buyer Meeting': '#f59e0b',
    'Legal & Procurement': '#ef4444'
  };

  const periodOptions = [
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' }
  ] as const;

  if (isLoading) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading stage trends...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 text-lg font-semibold font-inter mb-2">Error loading stage trends</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Please try again later</div>
          </div>
        </div>
      </div>
    );
  }

  if (!stageTrendsData || stageTrendsData.length === 0) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Stage Trends</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">Cumulative score (sum of completed criteria up to each stage) over time</p>
          </div>
          
          {/* Period Selector */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors font-inter ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-gray-900 dark:text-gray-100 text-lg font-semibold font-inter mb-2">No stage trends data available</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Try adjusting your filters or time period</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Stage Trends</h3>
              {isFetching && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
              )}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
              Shows the average cumulative score of deals in each stage per period, calculated by summing all completed exit criteria from stage 1 through the current stage and averaging across scored deals • A deal is counted if it entered the stage at any point during the period
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors font-inter ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>


      </div>



      {/* Individual Stage Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(stageColors).map(([stageName, color]) => {
          const data = stageData[stageName] || [];
          
          
          // Get the last period's data
          const lastPeriodData = data.length > 0 ? data[data.length - 1] : null;
          
          return (
            <div key={stageName} className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: color }}
                    ></div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 font-plus-jakarta">{stageName}</h4>
                  </div>
                  {/* Mini Legend */}
                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400 font-inter">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-0.5 bg-purple-600 rounded"></div>
                      <span>Avg Score</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-0.5" style={{ backgroundImage: "repeating-linear-gradient(to right, #9ca3af 0px, #9ca3af 3px, transparent 3px, transparent 6px)" }}></div>
                      <span>Deal Count</span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Mini Chart - Average Scores with Total Deals Line */}
              <div className="h-48 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={data}
                    margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                    <XAxis
                      dataKey="period_label"
                      stroke={chartTheme.axisStroke}
                      fontSize={10}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: chartTheme.tickFill }}
                    />
                    {/* Left Y-axis for Average Score (Cumulative 0-5) */}
                    <YAxis
                      yAxisId="score"
                      stroke={chartTheme.axisStroke}
                      fontSize={10}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: chartTheme.tickFill }}
                      domain={[0, 5]}
                    />
                    {/* Right Y-axis for Deal Count */}
                    <YAxis
                      yAxisId="deals"
                      orientation="right"
                      stroke={chartTheme.axisStroke}
                      fontSize={10}
                      fontFamily="Inter, sans-serif"
                      tick={{ fill: chartTheme.tickFill }}
                      domain={[0, 'dataMax']}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                              <p className="text-gray-900 dark:text-gray-100 font-semibold font-inter text-sm">{label}</p>
                              <div className="space-y-2 mt-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                                  <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">Average Score: </span>
                                  <span className="text-sm font-semibold text-purple-600 dark:text-purple-400 font-inter">{data.avg_score ? formatScore(data.avg_score) : '0.00'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                                  <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">Total Deals: </span>
                                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 font-inter">{data.deals_in_stage ?? 0}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 font-inter">
                                  <div>Scored Deals: {data.scored_deals}</div>
                                  <div>Total Amount: ${data.total_amount?.toLocaleString() || '0'}</div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {/* Primary line: Average Score */}
                    <Line
                      yAxisId="score"
                      type="monotone"
                      dataKey="avg_score"
                      stroke="#9333ea"
                      strokeWidth={3}
                      dot={{ fill: '#9333ea', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, stroke: '#9333ea', strokeWidth: 2, fill: '#ffffff' }}
                      connectNulls={false}
                    />
                    {/* Secondary line: Total Deals (dashed gray like Open Deals in Deal Scoring Trends) */}
                    <Line
                      yAxisId="deals"
                      type="monotone"
                      dataKey="deals_in_stage"
                      stroke="#9ca3af"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#9ca3af', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#9ca3af', strokeWidth: 2, fill: '#ffffff' }}
                      connectNulls={true}
                      isAnimationActive={true}
                      name="Deal Count"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StageTrendsChart;